// Layer 3 (Transformations): pure route predicates + the project-scoped
// module nav. Ported from ai-foundation's src/lib/project-sidebar-nav.ts
// (parity.P6c / D3) — seven static rows on every project.

import type { LucideIcon } from 'lucide-react';
import {
  CheckSquare,
  FileText,
  GitBranch,
  Home,
  LayoutDashboard,
  MessageCircle,
  Settings,
} from 'lucide-react';

export type ProjectSidebarNavItem = {
  segment:
    | ''
    | 'chat'
    | 'workflows'
    | 'editor'
    | 'dashboards'
    | 'tasks'
    | 'settings';
  label: string;
  Icon: LucideIcon;
};

export const PROJECT_SIDEBAR_NAV_ITEMS: readonly ProjectSidebarNavItem[] = [
  { segment: '', label: 'Home', Icon: Home },
  { segment: 'chat', label: 'AI Assistant', Icon: MessageCircle },
  { segment: 'workflows', label: 'Workflows', Icon: GitBranch },
  { segment: 'editor', label: 'Editor', Icon: FileText },
  { segment: 'dashboards', label: 'Dashboards', Icon: LayoutDashboard },
  { segment: 'tasks', label: 'Tasks', Icon: CheckSquare },
  { segment: 'settings', label: 'Settings', Icon: Settings },
] as const;

const PROJECT_ROUTE_SLUG_RE = /^\/projects\/([^/]+)/;
const PROJECT_CHAT_ROUTE_RE = /^\/projects\/([^/]+)\/chat(?:\/|$)/;
const PROJECT_SETTINGS_ROUTE_RE = /^\/settings\/projects\/([^/]+)/;

/** True on `/projects` exactly — the portfolio index. */
export function isPortfolioPath(pathname: string): boolean {
  return pathname === '/projects';
}

/** True on `/projects/<slug>/chat` — the project-scoped AI Assistant. */
export function isProjectChatPath(pathname: string): boolean {
  return PROJECT_CHAT_ROUTE_RE.test(pathname);
}

/** True on chat-home surfaces that mount Pinned/Recents in the sidebar (`/` and project chat). */
export function isChatThreadListPath(pathname: string): boolean {
  if (pathname === '/') return true;
  return isProjectChatPath(pathname);
}

/** True for `/settings/projects/[id]` — project settings, not personal settings. */
export function isProjectSettingsPath(pathname: string): boolean {
  return PROJECT_SETTINGS_ROUTE_RE.test(pathname);
}

/** True when the main panel is a full-width chat surface (chat-first framing). */
export function isChatFirstPath(pathname: string): boolean {
  return pathname === '/' || isProjectChatPath(pathname);
}

/** Extract the project slug from `/projects/<slug>…` or `/settings/projects/<slug>`. */
export function parseProjectSlugFromPathname(pathname: string): string | null {
  const fromProjects = PROJECT_ROUTE_SLUG_RE.exec(pathname);
  if (fromProjects?.[1]) return fromProjects[1];
  const fromSettings = PROJECT_SETTINGS_ROUTE_RE.exec(pathname);
  return fromSettings?.[1] ?? null;
}

/** A project's home surface — `/projects/<slug>`. */
export function projectHomeHref(slug: string): string {
  return `/projects/${slug}`;
}

export function projectSidebarHref(
  slug: string,
  segment: ProjectSidebarNavItem['segment'],
): string {
  if (segment === 'settings') {
    return `/settings/projects/${slug}`;
  }
  const base = `/projects/${slug}`;
  return segment === '' ? base : `${base}/${segment}`;
}

export function isProjectSidebarNavActive(
  pathname: string,
  slug: string,
  segment: ProjectSidebarNavItem['segment'],
): boolean {
  if (segment === 'settings') {
    return isProjectSettingsPath(pathname) && parseProjectSlugFromPathname(pathname) === slug;
  }
  const href = projectSidebarHref(slug, segment);
  if (segment === '') {
    return pathname === href;
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

export type ProjectsSidebarHeaderMeta = {
  title: string;
  back: { href: string; label: string };
};

/** True on `/projects*`, or project settings at `/settings/projects/[id]`. */
export function isProjectsSidebarPath(pathname: string): boolean {
  if (pathname.startsWith('/projects')) return true;
  return isProjectSettingsPath(pathname);
}

export function resolveProjectsSidebarHeader(
  pathname: string,
  activeProjectName?: string | null,
): ProjectsSidebarHeaderMeta | null {
  if (!isProjectsSidebarPath(pathname)) return null;

  const slug = parseProjectSlugFromPathname(pathname);
  if (slug) {
    const home = projectHomeHref(slug);
    const onProjectHome = pathname === home;
    return {
      back: onProjectHome
        ? { href: '/projects', label: 'Back to portfolio' }
        : { href: home, label: 'Back to project' },
      title: activeProjectName ?? 'Project',
    };
  }

  return {
    back: { href: '/', label: 'Back to workspace' },
    title: 'Portfolio',
  };
}
